import React, { useEffect, useRef, useState } from 'react';
import { Button, Modal, ModalBody, ModalFooter, Text } from '../ui/index';
import useEvent from '../hooks/useEvent';
import { type ScenarioSchedule } from '../Scenario';
import { useIsMobileView } from '../hooks/useMediaQuery';
import type { EventTimeline } from '../core/Timelines';

/**
 * This component displays a prompt modal when changes to an integration scenario
 * need to be committed. It helps ensure users are aware of and can commit the latest changes.
 *
 * Behavior:
 * - Shows only when the scenario has been updated after the most recent commit
 * - Check for show modal only once during the Planning page lifecycle to avoid disrupting the user experience
 * (when refrshing or navigating to planning page)
 */

export function IntegrationScenarioUpdateModal({
  openCommitModal,
  integrationScenario,
  selectScenario,
  changedTimelines,
}: {
  openCommitModal: (scenarioCID: ScenarioSchedule['clientId']) => void;
  integrationScenario: ScenarioSchedule;
  selectScenario: (scenarioCID: ScenarioSchedule['clientId']) => void;
  changedTimelines: EventTimeline[];
}) {
  const scenarioCID = integrationScenario.clientId;
  const scenarioName = integrationScenario.name;

  const [isModalVisible, setModalVisible] = useState<boolean>(false);
  const hasChecked = useRef(false);

  const shouldShowModal = useEvent(
    (updatedAt: string | null | undefined, commitedAt: number | undefined) => {
      if (
        !updatedAt ||
        !commitedAt ||
        integrationScenario.deletedAt ||
        changedTimelines.length === 0
      ) {
        return false;
      }

      return commitedAt < new Date(updatedAt).getTime();
    },
  );

  useEffect(() => {
    if (hasChecked.current) return;
    hasChecked.current = true;

    if (scenarioCID) {
      selectScenario(scenarioCID);
    }

    const shouldShowCommitModal = shouldShowModal(
      integrationScenario.updatedAt,
      integrationScenario?.masterCommitAt ?? undefined,
    );

    if (shouldShowCommitModal) {
      setModalVisible(true);
    }
  }, [
    integrationScenario,
    scenarioCID,
    shouldShowModal,
    changedTimelines,
    selectScenario,
  ]);

  const onClose = useEvent(() => {
    setModalVisible(false);
  });
  const onPress = useEvent(() => {
    if (!scenarioCID) {
      return;
    }
    openCommitModal(scenarioCID);
    setModalVisible(false);
  });

  const isMobileView = useIsMobileView();

  return (
    isModalVisible && (
      <Modal
        isOpen={true}
        onClose={onClose}
        width={isMobileView ? '350px' : '568px'}
        height="260px"
      >
        <ModalBody>
          <Text fontWeight="bold">
            {scenarioName || 'Integration'} scenario has been updated since last
            commit. Commit the new {scenarioName || 'integration'} scenario?
          </Text>
        </ModalBody>
        <ModalFooter>
          <Button variant="secondary" mr="2" onPress={onClose}>
            Cancel
          </Button>
          <Button variant="negative" onPress={onPress}>
            Commit
          </Button>
        </ModalFooter>
      </Modal>
    )
  );
}
